// Shared utilities for table display logic

interface TableRow {
  position: number;
  team: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  gf: number;
  ga: number;
  gd: number;
  points: number;
  isManagerTeam: boolean;
}

type TeamsToShow = 'auto' | 'all' | number;

const MAX_VISIBLE_ROWS = 10;

export interface VisibleRowsResult {
  rows: TableRow[];
  showTopEllipsis: boolean;
  showBottomEllipsis: boolean;
}

export const getVisibleRows = (
  tableData: TableRow[],
  totalTeams: number,
  teamsToShow: TeamsToShow
): VisibleRowsResult => {
  if (teamsToShow === 'all') {
    return { rows: tableData, showTopEllipsis: false, showBottomEllipsis: false };
  }

  if (typeof teamsToShow === 'number') {
    return {
      rows: tableData.slice(0, teamsToShow),
      showTopEllipsis: false,
      showBottomEllipsis: tableData.length > teamsToShow,
    };
  }

  // Auto mode - center on manager
  if (totalTeams <= MAX_VISIBLE_ROWS) {
    return { rows: tableData, showTopEllipsis: false, showBottomEllipsis: false };
  }

  const managerIndex = tableData.findIndex(row => row.isManagerTeam);
  const managerPosition = managerIndex !== -1 ? managerIndex : 0;

  if (managerPosition < 5) {
    return {
      rows: tableData.slice(0, MAX_VISIBLE_ROWS),
      showTopEllipsis: false,
      showBottomEllipsis: true,
    };
  }

  if (managerPosition >= totalTeams - 5) {
    return {
      rows: tableData.slice(-MAX_VISIBLE_ROWS),
      showTopEllipsis: true,
      showBottomEllipsis: false,
    };
  }

  const startIdx = managerPosition - 5;
  return {
    rows: tableData.slice(startIdx, startIdx + MAX_VISIBLE_ROWS),
    showTopEllipsis: true,
    showBottomEllipsis: true,
  };
};

// Common team name abbreviations for social media text
const TEAM_NAME_SHORTCUTS: Record<string, string> = {
  'Manchester United': 'Man Utd',
  'Manchester City': 'Man City',
  'Tottenham Hotspur': 'Spurs',
  'Wolverhampton Wanderers': 'Wolves',
  'AFC Bournemouth': 'Bournemouth',
  'Brighton & Hove Albion': 'Brighton',
  'Brighton and Hove Albion': 'Brighton',
  'West Ham United': 'West Ham',
  'Newcastle United': 'Newcastle',
  'Nottingham Forest': "Nott'm Forest",
  'Sheffield United': 'Sheffield Utd',
  'Leicester City': 'Leicester',
  'Leeds United': 'Leeds',
  'Crystal Palace': 'C. Palace',
  'Aston Villa': 'Aston Villa',
  'Queens Park Rangers': 'QPR',
  'West Bromwich Albion': 'West Brom',
  'Blackburn Rovers': 'Blackburn',
  'Bolton Wanderers': 'Bolton',
  'Swansea City': 'Swansea',
  'Stoke City': 'Stoke',
  'Cardiff City': 'Cardiff',
  'Huddersfield Town': 'Huddersfield',
  'Norwich City': 'Norwich',
  'Watford FC': 'Watford',
  'Burnley FC': 'Burnley',
  'Real Madrid CF': 'Real Madrid',
  'FC Barcelona': 'Barcelona',
  'Atlético Madrid': 'Atlético',
  'Athletic Bilbao': 'Athletic',
  'Real Sociedad': 'Sociedad',
  'Real Betis': 'Betis',
  'Villarreal CF': 'Villarreal',
  'Sevilla FC': 'Sevilla',
  'Valencia CF': 'Valencia',
  'Deportivo Alavés': 'Alavés',
  'RC Celta de Vigo': 'Celta Vigo',
  'RCD Espanyol': 'Espanyol',
  'Rayo Vallecano': 'Rayo',
  'Bayern München': 'Bayern',
  'Bayern Munich': 'Bayern',
  'Borussia Dortmund': 'Dortmund',
  'RB Leipzig': 'Leipzig',
  'Bayer Leverkusen': 'Leverkusen',
  'Eintracht Frankfurt': 'Frankfurt',
  'Borussia Mönchengladbach': 'Gladbach',
  'VfL Wolfsburg': 'Wolfsburg',
  'VfB Stuttgart': 'Stuttgart',
  'SC Freiburg': 'Freiburg',
  '1. FC Union Berlin': 'Union Berlin',
  'Hertha BSC': 'Hertha',
  'Paris Saint-Germain': 'PSG',
  'Olympique Marseille': 'Marseille',
  'Olympique Lyon': 'Lyon',
  'Olympique de Marseille': 'Marseille',
  'Olympique Lyonnais': 'Lyon',
  'AS Monaco': 'Monaco',
  'LOSC Lille': 'Lille',
  'Stade Rennais': 'Rennes',
  'OGC Nice': 'Nice',
  'RC Strasbourg': 'Strasbourg',
  'FC Nantes': 'Nantes',
  'Juventus FC': 'Juventus',
  'Inter Milan': 'Inter',
  'AC Milan': 'Milan',
  'SSC Napoli': 'Napoli',
  'AS Roma': 'Roma',
  'SS Lazio': 'Lazio',
  'Atalanta BC': 'Atalanta',
  'ACF Fiorentina': 'Fiorentina',
  'Torino FC': 'Torino',
  'Bologna FC': 'Bologna',
  'Hellas Verona': 'Verona',
  'US Sassuolo': 'Sassuolo',
  'Udinese Calcio': 'Udinese',
  'Empoli FC': 'Empoli',
  'UC Sampdoria': 'Sampdoria',
  'Genoa CFC': 'Genoa',
  'Cagliari Calcio': 'Cagliari',
  'Spezia Calcio': 'Spezia',
  'US Salernitana': 'Salernitana',
  'Ipswich Town': 'Ipswich',
};

export const shortenTeamName = (name: string): string => {
  // Check direct match first
  if (TEAM_NAME_SHORTCUTS[name]) {
    return TEAM_NAME_SHORTCUTS[name];
  }
  
  // Try removing common suffixes for matching
  const cleaned = name
    .replace(/\s+FC$/i, '')
    .replace(/\s+CF$/i, '')
    .replace(/\s+AFC$/i, '')
    .trim();
  
  if (TEAM_NAME_SHORTCUTS[cleaned]) {
    return TEAM_NAME_SHORTCUTS[cleaned];
  }
  
  // Return original if no match, but cap at 14 chars for display
  return name.length > 14 ? name.substring(0, 12) + '…' : name;
};